/**
 * ConversationFilter.js
 * Talkroute
 *
 * Created by Vel Genov on 3/16/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Grid } from "semantic-ui-react";
import { push } from "connected-react-router";

import {
  applyConversationFilter,
  closeConversation,
  findConversationById,
  createNewConversation,
  showConversationDetails,
  deleteConversation,
  deleteNewConversation,
  updateReadStatus,
  updateNewConversationFromPhoneNumber,
  updateNewConversationPhoneNumber,
  getConversationDownload,
} from "../../actions/conversationActions";
import { OUTGOING_TEXT_PHONE_NUMBER_SELECTED } from "../../actions/preferencesActions";
import {
  setOutgoingPhoneNumber,
  setNumberToCall,
  fetchBlockedNumbers,
  blockNumber,
  unblockNumber,
} from "../../actions/callActions";
import { getUserPermissions } from "../../actions/userActions";
import uiString from "../../constants/uiString";
import { getEditableForNumber, getMessagingPhoneNumbers } from "../../services/dataClasses/TalkroutePhoneNumber";
import validateArray from "../../helpers/arrayValidator";
import { formatPhoneNumber } from "../../services/dataClasses/CallData";
import ToPhoneNumber from "./ToPhoneNumber";
import {
  ConversationData,
  NEW_CONVERSATION_ID,
  WELCOME_CONVERSATION,
} from "../../services/dataClasses/ConversationData";
import routes, { routeUrlBuilders } from "../../constants/routes";
import ReadIndicator from "../voicemailComponents/ReadIndicator";
import NewConversationIcon from "@resources/icons/top-nav-compose.svg";
import CloseIcon from "@resources/icons/main-nav-history.svg";
import DeleteIcon from "@resources/icons/top-nav-delete.svg";
import DetailIcon from "@resources/icons/top-nav-details.svg";
import CallBackIcon from "@resources/icons/top-nav-call-back.svg";
import ConfirmModal from "../modal/ConfirmModal";
import userConstants from "../../constants/userConstants";
import UserPermissionsData from "../../services/dataClasses/UserPermissionsData";
import CallBlockIcon from "@resources/icons/top-nav-block.svg";
import CallUnblockIcon from "@resources/icons/top-nav-unblock.svg";
import AddContactIcon from "@resources/icons/phone-plus.svg";
import AddIcContactIcon from "@resources/icons/ic-add-contact.svg";
import DownloadButton from "@resources/icons/arrow-download-button.svg";
import ConfirmDialog from "@components/PresentationComponents/ConfirmDialog";
import DropdownComponent from "../PresentationComponents/DropdownComponent";
import { IconButton } from "@material-ui/core";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import useLocalStorage, { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import { IUserMetadata } from "@models/LocalStorageItems.models";
import ContactDialog from "@components/Contacts/ContactDialog";
import Tooltip from '@components/PresentationComponents/FormComponents/Tooltip';
import { domIds } from "@constants/testConstants";
import ContactDialogWithDropdown from "@components/Contacts/ContactDialogWithDropdown";
import CreateContactIcon from "@resources/icons/ic-add-contact.svg";
import CreateContactDialog from "@components/Contacts/CreateContactDialog";
import { ContactPhoneTypeEnum, IContact } from "@models/Contacts.models";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE, ADD_CONTACT, ADD_CONTACT_TOOLTIP_LABEL } from "@components/Contacts/constants";
import { addContactNameToConversation } from "@helpers/functions/contacts";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { withToastManager } from "react-toast-notifications";
import { getTextMessageAllowedForNumber, TalkroutePhoneNumber } from "@services/dataClasses/TalkroutePhoneNumber";

const CANT_BLOCK_NUMBER_MESSAGE = "Number can not be blocked at this time";
const BLOCK_NUMBER_SUCCESS = "The number has been blocked."
const UNBLOCK_NUMBER_SUCCESS = "The number has been unblocked."

/**
 * Place the delete confirmation into the view
 */
 export const DeleteConversationDialog = ({ 
  open,
  onDelete, 
  onClose, 
  selectedConversations }) => {
    
  const userId = useAppSelector(state => state.authentication.userId);
  const [metadata, setMetadata] = useLocalStorage<IUserMetadata[]>(localStorageItems.metadata, []);

  const formattedPhoneNumbers = selectedConversations.map((c) =>
  formatPhoneNumber(c.contactNumber)
  );
  const findWelcomeConversation = selectedConversations.find(conv => conv.conversationId === WELCOME_CONVERSATION)
  const handleConfirmation = () => {
    if (findWelcomeConversation) {
      const existingUserDataIndex = metadata.findIndex(userData => userData.userId === userId);
      if (existingUserDataIndex !== -1) {
        const newMetadata = metadata.map((userData, index) => 
          index === existingUserDataIndex ? ({...userData, isWelcomeConversationDeleted: true}) : userData
        )
        setMetadata(newMetadata)
      } else {
        setMetadata([...metadata, {
          userId: userId as number,
          isWelcomeVoicemailDeleted: false,
          isWelcomeVoicemailRead: false,
          isWelcomeConversationDeleted: true,
          isWelcomeConversationRead: false,
        }])
      }
    }
    onDelete();
  }

  return (
    <ConfirmDialog
      header={`${uiString.CONVERSATION_DELETE_CONFIRMATION_HEADER} - ${formattedPhoneNumbers}`}
      content={uiString.CONVERSATION_DELETE_CONFIRMATION_BODY}
      open={open}
      confirmButtonLabel={uiString.DELETE}
      onCancel={onClose}
      onConfirm={handleConfirmation}
    />
  );
}
interface IConversationFilterProps {
  conversations: any[];
  callHistory?: any[];
  filterBy: string;
  allPhoneNumbers?: any[];
  currentConversationId?: string;
  selectedOutgoingTextNumber?: string;
  outgoingTextPhoneNumbers: any[];
  conversationDraft: ConversationData;
  blockedNumbers: any[];
  userPermissions: UserPermissionsData;
  multipleSelectionMode: boolean;
  multipleSelectedConversations: string[];
  toastManager?: any;
  temporaryConversation: ConversationData | null;
  // redux actions
  applyConversationFilter: (conversations?: any[], filterBy?: string) => any;
  closeConversation: () => any;
  createNewConversation: (number?: string) => any;
  showConversationDetails: (
    callHistory: any,
    conversation: any,
    path: string
  ) => any;
  deleteConversation: (number?: string) => any;
  deleteNewConversation: () => any;
  updateReadStatus: ({conversationId, readStatus}: { conversationId: string, readStatus: boolean }) => any;
  updateNewConversationFromPhoneNumber: (data: any) => any;
  setOutgoingPhoneNumber: (number: string) => any;
  setNumberToCall: (number: string) => any;
  fetchBlockedNumbers: () => any;
  getUserPermissions: () => any;
  blockNumber: (number: string) => any;
  unblockNumber: (number: string) => any;
  push: (path: string) => any;
  history: any;
  updateNewConversationPhoneNumber: (phoneNumber: string) => void;
}

const buttonStyles = (isDisabled?: boolean) => ({
  padding: 10,
  bottom: 1,
  right: 2,
  opacity: isDisabled ? .45 : 1
})

class ConversationFilter extends React.Component<
  IConversationFilterProps,
  any
> {
  static defaultProps = {
    conversations: [],
    allPhoneNumbers: [],
    outgoingTextPhoneNumbers: [],
    callHistory: [],
    blockedNumbers: [],
  };

  /**
   * Display the filter dropdown only if there is more than 1 phone number to filter by
   */
  showFilterDropdown = () => {
    const { filterBy, allPhoneNumbers } = this.props;
    // Allow the use of a filter if we have at least 1 phone number
    const messagingPhoneNumbers = getMessagingPhoneNumbers(
      allPhoneNumbers
    );

    let dropdownOptions = [
      {
        value: uiString.ALL_MESSAGES,
        text: uiString.ALL_MESSAGES,
      },
    ];
    let disabled = true;

    if (validateArray(messagingPhoneNumbers)) {
      disabled = false;
      dropdownOptions = [
        ...dropdownOptions,
        ...messagingPhoneNumbers.map((entry) => ({
          value: entry.getPhoneNumber(),
          text: entry.getPhoneNumberFormatted(),
        })),
      ];
    }

    return (
      <DropdownComponent
        className="text-normal"
        disabled={disabled}
        value={filterBy}
        placeholder={uiString.ALL_MESSAGES}
        onChange={e => this.filterDropdownValueChanged(e.target.name, e.target.value)}
        smallIcon
        boldColor
        dropdownStyle={{marginLeft: 12, marginTop: 3, letterSpacing: 0.5}}
        options={dropdownOptions}
      />
    );
  };

  /**
   * Called when a filter value has been selected
   *
   */
  filterDropdownValueChanged = (name: string | undefined, value: unknown) => {
    this.applyFilter(value);
  };

  /**
   * Apply filter on all conversation entries
   *
   * @param filterBy the phone number to filter entried by
   */
  applyFilter = (filterBy) => {
    const {
      applyConversationFilter: applyConversationFilterLocal
    } = this.props;
    applyConversationFilterLocal(filterBy);
  };

  /**
   * Draw the new conversation button
   *
   * This button is either enabled or disabled based on isNewMessageButtonEnabled
   */
  showNewConversationButton = () => {
    const { isNewMessageButtonEnabled } = this.state;

    return (
      <Tooltip title="Compose New Conversation">
        <IconButton
          disabled={!isNewMessageButtonEnabled}
          style={{
            boxShadow: "none",
            padding: "0px",
            position: "absolute",
            right: "1em",
            opacity: !isNewMessageButtonEnabled ? .45 : 1
          }}
          onClick={() => this.onCreateNewConversation()}
        >
          <NewConversationIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Request to mark the conversation as read/unread
   * Currently marking conversations as read only
   */
  toggleReadStatus = () => {
    const {
      updateReadStatus: updateReadStatusLocal,
      currentConversationId: conversationId,
    } = this.props;
    const isEditable = this.getIsEditable();

    if (!isEditable) return;

    conversationId && updateReadStatusLocal({ conversationId, readStatus: true });
  };

  /**
   * Show a button to mark the conversation as read/unread
   */
  showMarkAsReadButton = () => {
    const { currentConversation } = this.state;
    const { currentConversationId } = this.props;
    const editable = this.getIsEditable();
    const isEditable = editable || this.state.isDefaultMessageSelected ? true : false;

    if (!currentConversationId) {
      return null;
    }

    if (currentConversation) {
      return (
        <div
          style={{
            margin: "1em",
            display: "inline-block",
            verticalAlign: "middle",
            height: "18px",
          }}
        >
          <ReadIndicator
            messageRead
            toggleCallBack={this.toggleReadStatus}
            allowHover={false}
            isHeader
            disabled={!isEditable}
          />
        </div>
      );
    }

    return null;
  };

  /**
   * Draws the close conversation button
   */
  showCloseConversationButton = () => (
    <Grid.Column textAlign="right" width={3} style={{ padding: "0px" }}>
      <IconButton
        onClick={() => this.onCloseConversation()}
        style={{ boxShadow: "none", padding: "0px" }}
      >
        <CloseIcon />
      </IconButton>
    </Grid.Column>
  );

  /**
   * Hide the details display
   */
  hideDetails = () => {
    this.props.push(routes.TEXT_MESSAGES);
  };

  /**
   * Called to fire a createNewConversation
   */
  onCreateNewConversation = () => {
    const {
      createNewConversation: createNewConversationLocal,
      selectedOutgoingTextNumber,
      push
    } = this.props;
    createNewConversationLocal(selectedOutgoingTextNumber);
    push(routeUrlBuilders.OPEN_CONVERSATION(NEW_CONVERSATION_ID));
  };

  /**
   * Called to process a click on the close conversation button
   * Creates a close conversation action
   */
  onCloseConversation = () => {
    const { closeConversation: closeConversationLocal } = this.props;
    
    closeConversationLocal();
  };

  /**
   *  Open add contact phone number dialog.
   */
  openDialogHandler = () => this.setState({ isAddPhoneDialogOpen: true });
  
  /**
   * Display a phone number dropdown
   */
  showPhoneNumberDropdown = (prefs) => {
    const { phoneNumbers, label, selectedValue, name } = prefs;
    const {
      updateNewConversationFromPhoneNumber: updateNewConversationFromPhoneNumberLocal,
    } = this.props;
    const placeholderValue = uiString.SELECT_PHONE_NUMBER;
    let dropdownOptions: any = [];

    const changeHandler = (name: string | undefined, value: unknown) => {
      this.setState({ selectedOutgoingTextFromNumber: value });

      if (this.props.currentConversationId === NEW_CONVERSATION_ID) {
        this.setState({ alternateSelectedNumber: value });
      }

      updateNewConversationFromPhoneNumberLocal(value);
    };

    if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
      dropdownOptions = phoneNumbers.map((entry) => ({
        value: entry.getPhoneNumber(),
        text: entry.getPhoneNumberFormatted(),
      }));
    }

    return (
      <div style={{ display: "inline-block", marginLeft: "10px" }}>
        {label}
        <DropdownComponent
          className="text-normal"
          name={name}
          value={selectedValue}
          placeholder={placeholderValue}
          onChange={e => changeHandler(e.target.name, e.target.value)}
          smallIcon
          options={dropdownOptions}
        />
      </div>
    );
  };

   /**
     * Method to check is text messaging allowed
     */
   getIsEditable = () => {
    const { conversations, currentConversationId } = this.props;
    let currentPhoneNumber = ""; 
  
    if (conversations && currentConversationId) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );
      if (currentConversation) {
        currentPhoneNumber = currentConversation.talkrouteNumber; 
      }
    }
  
    return getTextMessageAllowedForNumber(currentPhoneNumber, this.props.allPhoneNumbers);
  }

  /**
     * Method to check if calls can be made
     */
  getCanCall = () => {
    const { conversations, currentConversationId } = this.props;
    let currentPhoneNumber = ""; 
  
    if (conversations && currentConversationId) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );
      if (currentConversation) {
        currentPhoneNumber = currentConversation.talkrouteNumber; 
      }
    }
  
    return getEditableForNumber(currentPhoneNumber, this.props.allPhoneNumbers);
  }

  /**
   * Draws the "To: phoneNumber" field in the header
   */
  showToField = () => {
    const {
      conversationDraft,
      currentConversationId,
      outgoingTextPhoneNumbers,
    } = this.props;
    const { selectedOutgoingTextFromNumber, alternateSelectedNumber: stateAlternateSelectedNumber } = this.state;

    let alternateSelectedNumber = stateAlternateSelectedNumber;

    const editableTextPhoneNumbers = outgoingTextPhoneNumbers.filter(
      (numberEntry) => getTextMessageAllowedForNumber(numberEntry.phoneNumber, outgoingTextPhoneNumbers)
    );

    if (!stateAlternateSelectedNumber) {
      const isSelectedNumberEditable = editableTextPhoneNumbers.some(
        (numberEntry) => numberEntry.phoneNumber === selectedOutgoingTextFromNumber
      );
      alternateSelectedNumber = isSelectedNumberEditable
        ? selectedOutgoingTextFromNumber
        : editableTextPhoneNumbers[0]?.phoneNumber;
    }

    let whichNumber = '';
    if (currentConversationId == NEW_CONVERSATION_ID) {
      whichNumber = alternateSelectedNumber;
    } else {
      whichNumber = selectedOutgoingTextFromNumber;
    }

    if (currentConversationId === NEW_CONVERSATION_ID) {
      return (
        <Grid.Column
          width={14}
          textAlign="left"
          verticalAlign="middle"
          style={{ padding: "0px" }}
        >
          {this.toFieldNewConversation(conversationDraft)}
          {this.displayAddPhoneFromContactsButton()}
          {this.showPhoneNumberDropdown({
            phoneNumbers: editableTextPhoneNumbers,
            label: `${uiString.FROM}: `,
            selectedValue: whichNumber,
            name: OUTGOING_TEXT_PHONE_NUMBER_SELECTED,
          })}
        </Grid.Column>
      );
    }

    return (
      <Grid.Column
        width={8}
        textAlign="left"
        verticalAlign="middle"
        style={{ padding: "0px" }}
      >
        {this.toFieldExistingConversation()}
        {this.showMarkAsReadButton()}
      </Grid.Column>
    );
  };

  /**
   * Draw the "To:" field for new conversations
   *
   * The field is either editable or not, based on the availability of a contactNumber
   *
   * @param conversationDraft
   */
  toFieldNewConversation = (conversationDraft) => {
    const { contactNumber } = conversationDraft;
    const formattedPhoneNumber = formatPhoneNumber(contactNumber);
    return <ToPhoneNumber phoneNumber={formattedPhoneNumber} isDraft />;
  };

  displayAddPhoneFromContactsButton = () => (
    <Tooltip title="Insert phone number">
      <IconButton
        id={domIds.ADD_CONTACT_PHONE_BUTTON_CONVERSATIONS}
        onClick={() => this.openDialogHandler()}
        style={{ background: "none", boxShadow: "none", padding: 0, marginLeft: 8 }}
        disableRipple
        disableTouchRipple
      >
        <AddIcContactIcon style={{ width: 19, height: 22 }}/>
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the "To:" field for existing conversations
   */
  toFieldExistingConversation = () => {
    const { conversations, currentConversationId, temporaryConversation } = this.props;

    if (!currentConversationId) {
      return null;
    }

    const conversationIndex = findConversationById({
      conversations,
      conversationId: currentConversationId,
    });

    const conversation = conversations[conversationIndex];
    if (conversation !== undefined) {
      const { contactNumber } = conversation;
      const formattedPhoneNumber = formatPhoneNumber(contactNumber);

      return <ToPhoneNumber phoneNumber={formattedPhoneNumber} />;
    }

    !temporaryConversation && this.onCloseConversation();
    return null;
  };

  /**
   * Show the message heading
   * This is either a "New Message" title or
   * A section of buttons related to existing messages
   */
  showMessageHeading = () => {
    const { currentConversationId } = this.props;

    if (!currentConversationId) {
      return null;
    }

    if (currentConversationId === NEW_CONVERSATION_ID) {
      return this.showNewMessageHeading();
    }

    return this.showMessageButtons();
  };

  /**
   * Show the buttons related to an existing message
   */
  showMessageButtons = () => {
    const { multipleSelectionMode } = this.props;
    return (
      <Grid.Column
        width={8}
        style={{ padding: "0px" }}
      >
        <div style={{ padding: "0px", display: "flex", justifyContent: "flex-end", maxHeight: 39 }}>
          {!multipleSelectionMode && this.detailsButton()}
          {!multipleSelectionMode && this.callBackButton()}
          {!multipleSelectionMode && this.callBlockButton()}
          {!this.state.alreadyInContacts && !multipleSelectionMode && this.addContactButton()}
          {!multipleSelectionMode && this.createContactButton()}
          {!multipleSelectionMode && this.createDownloadButton()}
          {this.deleteButton()}
        </div>
      </Grid.Column>
    );
  };

  /**
   * Show a New Message Delete button
   */
  showNewMessageHeading = () => (
    <Grid.Column
      width={2}
      style={{ padding: "0px", display: "flex", justifyContent: "flex-end" }}
    >
      {this.deleteNewConversationButton()}
    </Grid.Column>
  );

  /**
   * Shows the right part of the header, when a conversation has been selected
   */
  showSelectedConversationHeader = () => {
    const { currentConversationId } = this.props;

    return (
      <Grid.Column
        verticalAlign="middle"
        style={{
          clear: "both",
          textAlign: "middle",
          flex: "1",
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <Grid style={{ margin: "0px" }}>
          {this.showToField()}
          {this.showMessageHeading()}
        </Grid>
      </Grid.Column>
    );
  };

  /**
   * Draw the delete new conversation button
   */
  deleteNewConversationButton = () => {

    return (
      <Tooltip title="Delete">
        <IconButton
          onClick={() => this.deleteNewConversation()}
          style={buttonStyles()}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the delete button
   */
  deleteButton = () => {

    const editable = this.getIsEditable();
    const isEditable = editable || this.state.isDefaultMessageSelected ? true : false;

    return (
      <Tooltip title="Delete">
        <IconButton
          onClick={() => this.requestToDeleteConversation()}
          style={buttonStyles(!isEditable)}
          disabled={!isEditable}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the call block button
   */
  callBlockButton = () => {
    const { blockedNumbers, conversations, currentConversationId } = this.props;

    let blocked = false;
    if (conversations) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );

      if (currentConversation) {
        const { contactNumber } = currentConversation;

        blocked = blockedNumbers.some((number) => number === contactNumber);
      }
    }

    return (
      <Tooltip title={blocked ? 'Unblock' : 'Block'}>
        <IconButton
          onClick={() => this.callBlock()}
          style={buttonStyles(this.state.isDefaultMessageSelected)}
          disabled={this.state.isDefaultMessageSelected}
        >
          {blocked ? <CallUnblockIcon /> : <CallBlockIcon />}
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the details button
   */
  detailsButton = () => (
    <Tooltip title="Details">
      <IconButton
        onClick={() => this.showDetails()}
        style={buttonStyles()}
      >
        <DetailIcon />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the call back button
   */
  callBackButton = () => {
    const canCall = this.getCanCall();
    return (
      <Tooltip title="Call">
        <IconButton
          onClick={() => this.callBack()}
          style={buttonStyles(this.state.isDefaultMessageSelected || !canCall)}
          disabled={this.state.isDefaultMessageSelected || !canCall}
        >
          <CallBackIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the add contact button 
   */
  addContactButton = () => (
    <Tooltip title={ADD_CONTACT_TOOLTIP_LABEL}>
      <IconButton
        onClick={() => this.setState({ isContactDialogOpen: true })}
        style={buttonStyles(this.state.isDefaultMessageSelected)}
        disabled={this.state.isDefaultMessageSelected}
      >
        <AddContactIcon />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the create contact button 
   */
  createContactButton = () => (
    <Tooltip title={this.state.alreadyInContacts ? "Edit Contact" : "Create Contact"}>
      <IconButton
        onClick={() => this.setState({ isCreateContactDialogOpen: true })}
        style={buttonStyles(this.state.isDefaultMessageSelected)}
        disabled={this.state.isDefaultMessageSelected}
      >
        <CreateContactIcon style={{ width: 19, height: 19 }} />
      </IconButton>
    </Tooltip>
  );


  /**
   * Draw the download conversation button 
   */
  createDownloadButton = () => (
    <Tooltip title="Download Conversation">
      <IconButton
        onClick={() => this.csvDownload()}
        style={buttonStyles(this.state.isDefaultMessageSelected)}
        disabled={this.state.isDefaultMessageSelected}
      >
        <DownloadButton style={{ width: 19, height: 19 }} />
      </IconButton>
    </Tooltip>
  );


    /**
   * Show a toast message
   *
   * @param message the message to display
   */
     showToast = (message, appearance, persist?: boolean, disableDismiss?: boolean) => {
      const { toastManager } = this.props;
  
      toastManager.add(<div>{message}</div>, {
        appearance: appearance,
        autoDismiss: !persist,
        pauseOnHover: false,
        disableDismiss
      });
    };
  

  /**
   * Block/unblock the call for phone number
   */
  callBlock = () => {
    const {
      userPermissions,
      blockedNumbers,
      conversations,
      currentConversationId,
      blockNumber: blockNumberLocal,
      unblockNumber: unblockNumberLocal,
    } = this.props;

    if (conversations) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );

      if (currentConversation) {
        const { contactNumber } = currentConversation;

        if (userPermissions) {
          const { itemBlockedNumber } = userPermissions;

          if (itemBlockedNumber === userConstants.permissions.EDIT) {
            // Set the blocked numbers
            const blocked = blockedNumbers.some(
              (number) => number === contactNumber
            );

            if (blocked) {
              this.showToast(UNBLOCK_NUMBER_SUCCESS, "success", false, false);
              return unblockNumberLocal(contactNumber);
            }
            
            this.showToast(BLOCK_NUMBER_SUCCESS, "success", false, false);
            return blockNumberLocal(contactNumber);
          }
        }

        this.setState({ isShowBlockPermissionError: true });
      }
    }
  };

  /**
   * Request to show details about a conversation
   */
  showDetails = () => {
    const {
      currentConversationId,
      showConversationDetails,
      callHistory,
      conversations,
      temporaryConversation,
      push,
    } = this.props;
    if (conversations) {
      const currentConversation = temporaryConversation && temporaryConversation.conversationId === currentConversationId ?
      temporaryConversation : 
      conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );

      if (currentConversation) {
        showConversationDetails(
          callHistory,
          currentConversation,
          routes.TEXT_MESSAGES
        );
        push(routeUrlBuilders.OPEN_CONVERSATION(currentConversation.conversationId, true));
      }
    }
  };

  /**
   * Get a call ready. Pass the numberToCall, outgoingPhoneNumber to Keypad
   */
  callBack = () => {
    const {
      conversations,
      currentConversationId,
      setOutgoingPhoneNumber: setOutgoingPhoneNumberLocal,
      setNumberToCall: setNumberToCallLocal,
      push,
    } = this.props;

    if (conversations) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );

      if (currentConversation) {
        const { contactNumber, talkrouteNumber } = currentConversation;

        setOutgoingPhoneNumberLocal(talkrouteNumber);
        setNumberToCallLocal(contactNumber);

        push(routes.KEYPAD);
      }
    }
  };

  /**
   * Download CSV of converstation
   */
  csvDownload = () => {
    const { currentConversationId } = this.props;

    getConversationDownload(currentConversationId)
    .catch(error => {
      console.error('Error downloading CSV:', error);
    });
  };

  /**
   * Called to open the context popup menu
   */
  popupOpen = () => this.setState({ popupOpen: true });

  /**
   * Called to close the context popup menu
   */
  popupClose = () => this.setState({ popupOpen: false });

  /**
   * Get current selected conversations
   */
  getSelectedConversations = () => {
    const {
      currentConversationId,
      conversations,
      multipleSelectionMode,
      multipleSelectedConversations,
      temporaryConversation
    } = this.props;
    return multipleSelectionMode
      ? conversations.filter((conversation) =>
          multipleSelectedConversations.includes(conversation.conversationId)
        )
      : temporaryConversation?.conversationId === currentConversationId ? [temporaryConversation] : 
      conversations.filter(
        (conversation) =>
          conversation.conversationId === currentConversationId
      );;
  };


  /**
   * Request to delete a conversation with Id
   *
   */
  requestToDeleteConversation = () => {
    this.popupOpen();
  };

  /**
   * Delete this conversation
   * @param conversationId
   */
  deleteConversations = () => {
    const { deleteConversation: deleteConversationLocal, conversations, history, temporaryConversation, currentConversationId } = this.props;
    const selectedConversations = this.getSelectedConversations();
    
    if (selectedConversations.length === 0 && temporaryConversation?.conversationId === currentConversationId) {
      deleteConversationLocal(currentConversationId);
    } else {
    selectedConversations.forEach((conversation: ConversationData) =>
      deleteConversationLocal(conversation.conversationId)
    );

    if (conversations.length) {
      const firstConversation = conversations.find((conversation) => {
        return !selectedConversations.find((selectedConversation) => {
          return selectedConversation.conversationId === conversation.conversationId;
        });
      }
      );
      if (firstConversation) {
        history.push(routeUrlBuilders.OPEN_CONVERSATION(firstConversation.conversationId));
      }
    }
  }
    this.popupClose();
  };

  deleteNewConversation = () => {
    const {
      deleteNewConversation: deleteNewConversationLocal,
      selectedOutgoingTextNumber,
      history,
      conversations,
    } = this.props;

    deleteNewConversationLocal();
    if (conversations.length) {
      history.push(routeUrlBuilders.OPEN_CONVERSATION(conversations[0].conversationId))
    }
    this.setState({
      selectedOutgoingTextFromNumber: selectedOutgoingTextNumber,
    });
  };

  /**
   * Cancel deleting this conversation
   */
  cancelDeletingConversation = () => {
    this.popupClose();
  };

  /**
   * Load the blocked phone numbers
   */
  loadBlockedNumbers = () => {
    const { fetchBlockedNumbers: fetchBlockedNumbersLocal } = this.props;
    fetchBlockedNumbersLocal();
  };

  /**
   * Request a getting user permissions
   */
  loadUserPermissions = () => {
    const { getUserPermissions: getUserPermissionsLocal } = this.props;
    getUserPermissionsLocal && getUserPermissionsLocal();
  };

  /**
   * Show the call block permission error dialog
   */
  showBlockPermissionError = () => {
    const { isShowBlockPermissionError } = this.state;

    if (isShowBlockPermissionError) {
      return (
        <ConfirmModal
          title={uiString.blockPermissionErrorModal.TITLE}
          desc={uiString.blockPermissionErrorModal.DESC}
          buttonLabel={uiString.OK}
          clickActionCallback={() =>
            this.setState({ isShowBlockPermissionError: false })
          }
        />
      );
    }

    return null;
  };

  /**
   * Draws the main layout for the header and filter menu
   */
  showHeaderLayout = () => {
    const phoneNumber = this.props.currentConversationId?.split('-')[1] ?? '';
    const { 
      updateNewConversationPhoneNumber: updateNewConversationPhoneNumberLocal 
    } = this.props;
    const submitPhoneToField = (phoneNumber: string) => {
      updateNewConversationPhoneNumberLocal(phoneNumber);
      this.setState({ isAddPhoneDialogOpen: false });
    };

    return (
      <Grid style={{ height: "60px", minHeight: "60px", flex: "1", margin: "0" }}>
        {this.showBlockPermissionError()}
        <Grid.Column
          textAlign="center"
          verticalAlign="middle"
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
          }}
          className="middle-cell"
        >
          {this.showFilterDropdown()}
          {this.showNewConversationButton()}
        </Grid.Column>
        {this.showSelectedConversationHeader()}
        <ContactDialog 
          open={this.state.isContactDialogOpen}
          onClose={() => this.setState({ isContactDialogOpen: false })}
          phoneNumber={phoneNumber}
        />
        <ContactDialogWithDropdown
          open={this.state.isAddPhoneDialogOpen}
          onClose={() => this.setState({ isAddPhoneDialogOpen: false })}
          onClick={submitPhoneToField}
        />
        <CreateContactDialog
          open={this.state.isCreateContactDialogOpen}
          onClose={() => this.setState({ isCreateContactDialogOpen: false })}
          contactData={this.state.defaultContactData}
        />
        <ContactCreateDialogManagement 
          open={this.state.isCreateContactDialogOpen}
          close={() => this.setState({ isCreateContactDialogOpen: false })}
          defaultContactData={this.state.defaultContactData}
          conversationId={this.props.currentConversationId}
          conversationList={this.props.conversations}
          onContactAvailable={contact => this.setState({alreadyInContacts: Boolean(contact)})}
        />
        <DeleteConversationDialog 
          onClose={this.cancelDeletingConversation}
          onDelete={this.deleteConversations} 
          open={this.state.popupOpen} 
          selectedConversations={this.getSelectedConversations()}
        />
      </Grid>
    );
  };

  /**
   * Disable the button to create new messages
   */
  disableNewMessageButton = () => {
    this.setState({ isNewMessageButtonEnabled: false });
  };

  /**
   * Enable the button to create new messages
   */
  enableNewMessageButton = () => {
    this.setState({ isNewMessageButtonEnabled: true });
  };

  enableDetailButtons = () => {
    this.setState({ isDefaultMessageSelected: false });
  }

  disableDetailButtons = () => {
    this.setState({ isDefaultMessageSelected: true });
  }

  /**
   * Update the reference to the current message
   */
  updateCurrentMessage = () => {
    const { currentConversationId, conversations } = this.props;
    if (conversations && currentConversationId) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );

      if (currentConversation) {
        const { currentConversation: prevConversation } = this.state;

        if (currentConversation !== prevConversation) {
          this.setState({ currentConversation });
        }
      }
    }
  };

  constructor(props) {
    super(props);

    const { selectedOutgoingTextNumber } = this.props;
    const enableNewMessageButton = selectedOutgoingTextNumber !== null;

    this.state = {
      isNewMessageButtonEnabled: enableNewMessageButton,
      popupOpen: false,
      currentConversation: null,
      selectedOutgoingTextFromNumber: selectedOutgoingTextNumber,
      alternateSelectedNumber: '',
      isShowBlockPermissionError: false,
      isContactDialogOpen: false,
      isAddPhoneDialogOpen: false,
      isCreateContactDialogOpen: false,
      isDefaultMessageSelected: false,
      alreadyInContacts: false,
      defaultContactData: {
        name: "",
        company: "",
        emails: [""],
        role: "",
        url: "",
        notes: "",
        id: "",
        phoneNumbers: [{
          phoneNumber: '',
          type: ContactPhoneTypeEnum.MOBILE,    
          id: "",
        }],
      }
    };
  }

  componentDidMount() {
    const { currentConversationId } = this.props;

    this.updateCurrentMessage();
    this.loadBlockedNumbers();
    this.loadUserPermissions();

    if (currentConversationId === WELCOME_CONVERSATION) {
      this.disableDetailButtons();
    } else {
      this.enableDetailButtons();
    }
  }

  /**
   * Check if the selectedOutgoingTextNumber has changed and disable/enable
   * the new message button accordingly
   *
   * @param {*} prevProps the previous props
   */
  componentDidUpdate(prevProps) {
    const {
      selectedOutgoingTextNumber: prevSelectedOutgoingTextNumber,
      currentConversationId: prevCurrentConversationId,
    } = prevProps;
    const { selectedOutgoingTextNumber, currentConversationId } = this.props;
    if (this.state.defaultContactData.id != currentConversationId) {
      this.setState(state => ({
        ...state,
        defaultContactData: {
          ...state.defaultContactData,
          id: currentConversationId ?? null,
          phoneNumbers: [{
            phoneNumber: currentConversationId?.split('-')[1] ?? '',
            type: ContactPhoneTypeEnum.MOBILE,    
            id: "",
          }],
        }
      }))
    }

    if (selectedOutgoingTextNumber !== prevSelectedOutgoingTextNumber) {
      // If the outgoing text number was updated
      if (selectedOutgoingTextNumber === null) {
        this.disableNewMessageButton();
      } else {
        this.enableNewMessageButton();
      }
    }

    this.updateCurrentMessage();

    // If the conversationID changed, update the state of the callback button accordingly
    if (prevCurrentConversationId !== currentConversationId) {
      if (currentConversationId === WELCOME_CONVERSATION) {
        this.disableDetailButtons();
      } else {
        this.enableDetailButtons();
      }
    }
  }

  /**
   * Close the conversation draft before the component unmounts
   */
  componentWillUnmount() {}

  render() {
    return this.showHeaderLayout();
  }
}

const ContactCreateDialogManagement = ({open, close, conversationId, onContactAvailable, defaultContactData, conversationList} :
  {
    open: boolean,
    close: () => void,
    conversationId: string | undefined | null,
    onContactAvailable: (contact?: IContact) => void,
    defaultContactData: IContact,
    conversationList: ConversationData[]
  }) => {
  const {data: contactList} = useGetAllContactsQuery({ params: { 
    pageSize: GET_TEN_THOUSAND_CONTACTS,
    page: CONTACTS_INITIAL_PAGE
  } });
  const conversation = conversationList.find(c => c.conversationId === conversationId);
  const conversationContact = useMemo(() => contactList && conversation ?
  addContactNameToConversation(contactList, [conversation])[0]?.contact:
  conversation?.contact, [contactList, conversation])

  useEffect(() => {
    onContactAvailable(conversationContact)
  }, [conversationContact])

  return <>
        <CreateContactDialog
          open={open}
          onClose={close}
          contactData={conversationContact ? conversationContact : defaultContactData}
        />
  </>
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  filterBy: state.conversations.filterBy,
  callHistory: state.callHistory.data,
  conversations: state.conversations.data,
  allPhoneNumbers: state.preferences.allPhoneNumbers,
  outgoingTextPhoneNumbers: state.preferences.outgoingTextPhoneNumbers,
  currentConversationId: state.conversations.selectedConversationId,
  selectedOutgoingTextNumber: state.preferences.selectedOutgoingTextNumber,
  conversationDraft: state.conversations.conversationDraft,
  blockedNumbers: state.callReducer.blockedNumbers,
  userPermissions: state.authentication.userPermissions,
  multipleSelectionMode: state.conversations.multipleSelectionMode,
  multipleSelectedConversations:
    state.conversations.multipleSelectedConversations,
  temporaryConversation: state.conversations.temporaryConversation,
});

export default withToastManager(connect(mapStateToProps, {
  push,
  applyConversationFilter,
  closeConversation,
  createNewConversation,
  showConversationDetails,
  deleteConversation,
  deleteNewConversation,
  updateReadStatus,
  updateNewConversationFromPhoneNumber,
  setOutgoingPhoneNumber,
  setNumberToCall,
  fetchBlockedNumbers,
  getUserPermissions,
  blockNumber,
  unblockNumber,
  updateNewConversationPhoneNumber,
})(ConversationFilter));
